import { feedbackcard, YesObject } from '@yes.technology/react-toolkit'
import { useCallback, useEffect, useId, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import useNavigateMergingState from 'shared/hooks/useNavigateMergingState/useNavigateMergingState'
import deepCopy from 'shared/utils/object/deepCopy'
import useLocationState from 'site/Renderer/components/FilterFields/hooks/useLocationState'
import { useSiteState } from 'siteState/shared'
import { useFetchYesObjects } from 'yesObject/shared'
import getRelationCounterpartField from '../../lib/getRelationCountertpartField/getRelationCounterpartField'
import RelationAutocomplete from '../RelationAutocomplete/RelationAutocomplete'
import RelationClassTable from './RelationClassTable'
import { SelectAndLinkOptions } from 'site/Renderer/components/DataTables/types'
import { RelationClassProps } from './RelationClass.types'
import { useTranslation } from 'react-i18n-lite'

const RelationClass = ({
  fieldType = 'table',
  relationFieldRelation,
  fieldValues,
  layoutClassificationUuid,
  hierarchyLevel,
  actionButton,
  field,
  informational,
  navigationSliderBackgroundColor,
  navigationSliderColor,
  fieldSettings,
  hideInsertAction = false,
  hideSelectAndLinkAction = false,
  hideDocumentUploadAction = true,
  selectAndLinkTargetPath,
  uploadAcceptedFileTypes,
  isEditing = false,
  edit,
  onChange,
  save,
  changeAndSave
}: RelationClassProps) => {
  const objectclassUuid = relationFieldRelation?.uuid_objectclass || ''

  const relationCounterpartField = getRelationCounterpartField({
    relationFieldRelation,
    fieldValues
  })

  const [relationCounterpartColumn] = Object.keys(
    relationCounterpartField || {}
  )

  const {
    yesObjects,
    isFetching,
    pagination,
    totalObjects,
    setPage,
    sortOptions,
    setSortOptions,
    fetchYesObjects: refetchObjects
  } = useFetchYesObjects({
    filterBody: {
      query: {
        $and: [
          {
            [`${relationCounterpartColumn}.uuid`]: {
              $in: [`${fieldValues?.uuid}`]
            },
            'situation.des': {
              $gte: '40'
            }
          }
        ]
      },
      objectclass: objectclassUuid
    },
    enabled: !!relationCounterpartColumn && !!objectclassUuid
  })

  const { persistentSelection, targetField } = useLocationState()

  const currentField = field?.column_json || ''
  const currentFieldPersistentSelection =
    persistentSelection?.ObjectView?.[currentField]

  const [_, setLockObjectViewObject] = useSiteState<boolean>(
    'lock-object-view-object'
  )

  const id = useId()

  const relationId = targetField || id

  const tableId = currentField || id

  const onScrollToRelationTable = useCallback(() => {
    const relationTable = document.getElementById(relationId)

    if (relationTable) {
      relationTable.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }, [relationId])

  const [unsavedObjects, setUnsavedObjects] = useState<YesObject[]>([])
  const [savedObjectsToRemove, setSavedObjectsToRemove] = useState<YesObject[]>(
    []
  )

  useEffect(() => {
    if (!isEditing && savedObjectsToRemove.length) {
      setSavedObjectsToRemove([])
    }
  }, [isEditing, savedObjectsToRemove.length])

  useEffect(() => {
    if (
      fieldType === 'card' &&
      !isEditing &&
      !isFetching &&
      unsavedObjects.length &&
      unsavedObjects.some((unsavedObject) =>
        yesObjects.every(
          (savedObject) => savedObject.uuid !== unsavedObject.uuid
        )
      )
    ) {
      edit()
    }
  }, [
    edit,
    isEditing,
    unsavedObjects.length,
    fieldType,
    isFetching,
    unsavedObjects,
    yesObjects
  ])

  const navigateMergingState = useNavigateMergingState()
  const location = useLocation()

  useEffect(() => {
    if (!currentFieldPersistentSelection) {
      setUnsavedObjects((unsavedObjects) =>
        unsavedObjects.length ? [] : unsavedObjects
      )

      return
    }

    setUnsavedObjects((unsavedObjects) => {
      const objectsToLink = currentFieldPersistentSelection.filter(
        (objectToLink) =>
          yesObjects.every(
            (savedObject) => savedObject.uuid !== objectToLink.uuid
          )
      )

      if (!objectsToLink.length && !unsavedObjects.length) return unsavedObjects

      onChange(currentField, [...yesObjects, ...objectsToLink])

      return objectsToLink
    })
  }, [currentField, currentFieldPersistentSelection, onChange, yesObjects])

  useEffect(() => {
    if (!currentField || !currentFieldPersistentSelection) return

    const wereObjectsLinked = currentFieldPersistentSelection.every(
      (objectToLink) =>
        yesObjects.some((savedObject) => savedObject.uuid === objectToLink.uuid)
    )

    if (!wereObjectsLinked) return

    const newPersistentSelection = deepCopy(persistentSelection)
    delete newPersistentSelection.ObjectView?.[currentField]

    navigateMergingState('.' + location.search, {
      state: {
        ...location.state,
        persistentSelection: newPersistentSelection,
        targetComponent: undefined,
        targetField: undefined
      },
      replace: true
    })

    onScrollToRelationTable()
  }, [
    currentField,
    currentFieldPersistentSelection,
    location.search,
    location.state,
    navigateMergingState,
    persistentSelection,
    yesObjects,
    onScrollToRelationTable
  ])

  useEffect(() => {
    if (isFetching || !unsavedObjects.length) return

    onScrollToRelationTable()
  }, [unsavedObjects, isFetching, onScrollToRelationTable])

  const tableObjects = useMemo(
    () => [
      ...yesObjects.filter(
        (object) =>
          !savedObjectsToRemove.some(
            (objectToRemove) => objectToRemove.uuid === object.uuid
          )
      ),
      ...unsavedObjects
    ],
    [yesObjects, unsavedObjects, savedObjectsToRemove]
  )

  const title = field?.display_text.des || ''

  const selectAndLinkOptions: SelectAndLinkOptions = useMemo(
    () => ({
      targetComponent: 'ObjectView',
      targetField: currentField,
      selectedOptions: currentFieldPersistentSelection
    }),
    [currentField, currentFieldPersistentSelection]
  )

  const { t } = useTranslation()

  if (fieldType === 'card') {
    const handleAutocompleteChange = (options: YesObject[]) => {
      onChange(currentField, options)

      const newSelectedOptions = options.filter(
        (option) => !tableObjects.find((object) => object.uuid === option.uuid)
      )
      setUnsavedObjects((oldUnsavedObjects) => [
        ...oldUnsavedObjects.filter((object) =>
          options.find((option) => option.uuid === object.uuid)
        ),
        ...newSelectedOptions
      ])

      setSavedObjectsToRemove(
        yesObjects.filter(
          (object) => !options.some((option) => option.uuid === object.uuid)
        )
      )
    }

    return (
      <RelationAutocomplete
        {...{
          objectclassUuid,
          selectAndLinkOptions,
          selectAndLinkTargetPath,
          hideSelectAndLinkAction,
          title,
          navigationSliderBackgroundColor,
          navigationSliderColor
        }}
        id={tableId}
        multiplicity={relationFieldRelation?.multiplicity || 'N:N'}
        onChange={handleAutocompleteChange}
        selectedObjects={tableObjects}
        informational={informational || !isEditing}
      />
    )
  }

  const handleSave = () => {
    save(currentField).then(() => refetchObjects())
  }

  const unlinkObjects = async (objects: YesObject[]) => {
    if (!currentField) return

    setLockObjectViewObject(true)

    const { savedRelations, unsavedRelations } = objects.reduce(
      (
        acc: { savedRelations: YesObject[]; unsavedRelations: YesObject[] },
        object
      ) => {
        if (yesObjects.some((yesObject) => yesObject.uuid === object.uuid)) {
          acc.savedRelations.push(object)
        } else {
          acc.unsavedRelations.push(object)
        }

        return acc
      },
      {
        savedRelations: [],
        unsavedRelations: []
      }
    )

    if (savedRelations.length > 0) {
      changeAndSave(
        currentField,
        yesObjects.filter((yesObject) => {
          return !savedRelations.some(
            (object) => object.uuid === yesObject.uuid
          )
        })
      )
    }

    if (
      unsavedRelations.length > 0 &&
      persistentSelection?.ObjectView?.[currentField]
    ) {
      const newPersistentSelection = deepCopy(persistentSelection)

      newPersistentSelection.ObjectView =
        newPersistentSelection.ObjectView || {}

      newPersistentSelection.ObjectView = {
        ...newPersistentSelection.ObjectView,
        [currentField]: newPersistentSelection.ObjectView[currentField]?.filter(
          (persistentSelection) =>
            !unsavedRelations.some(
              (object) => object.uuid === persistentSelection?.uuid
            )
        )
      }

      navigateMergingState('.' + location.search, {
        state: {
          ...location.state,
          persistentSelection: newPersistentSelection
        },
        replace: true
      })

      if (savedRelations.length === 0) {
        feedbackcard(t('data-tables.success'), {
          type: 'success',
          message: t('data-tables.success')
        })
      }
    }
  }

  return (
    <RelationClassTable
      onSave={handleSave}
      onUnlink={unlinkObjects}
      {...{
        title,
        informational,
        objectclassUuid,
        tableObjects,
        currentField,
        currentFieldPersistentSelection,
        fieldSettings,
        tableId,
        layoutClassificationUuid,
        isFetching,
        hierarchyLevel,
        actionButton,
        hideInsertAction,
        hideSelectAndLinkAction,
        hideDocumentUploadAction,
        selectAndLinkTargetPath,
        id: tableId,
        uploadAcceptedFileTypes,
        selectAndLinkOptions,
        pagination,
        sortOptions,
        totalObjects,
        unsavedObjects,
        setSortOptions,
        setPage,
        refetchObjects,
        navigationSliderBackgroundColor,
        navigationSliderColor
      }}
    />
  )
}

export default RelationClass
